import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { AuthFrame, LoginCard, SetupCard } from "../components/Auth";
import { Button } from "../components/Button";
import { Alert } from "../components/Card";
import { Spinner } from "../components/Spinner";
import { TotpEnrollGate, TotpLoginCard, writeSavedLoginId } from "@mops/totp-ui-v2";
import { AuthError, ENROLL_REQUIRED, LOGGED_OUT, SSO_LOGIN_URL, authApi, authErrText, watchUnauthorized } from "../lib/auth";
import { routeParam } from "../lib/route";
import type { AuthState, Me } from "../lib/auth";
import { tx } from "../i18n";

/* ══ 문지기 — 로그인 전이면 첫 설정 · 로그인 화면, 들어왔으면 앱 ══
   **판정은 서버다**(`docs/decisions/auth.md` §4). 화면이 보는 것은 둘뿐이다 —
   `GET /auth/state`(첫 설정이 남았나)와 `GET /auth/me`(지금 누구인가). 화면을 가리는 것은
   편의일 뿐 보안이 아니다: 가린 화면의 API 는 관문이 따로 401·403 으로 막는다.

   들어온 뒤에 어느 화면이든 401 을 받으면(세션 만료 · 다른 곳에서 막힘) 여기로 돌아온다
   (`watchUnauthorized`). 화면마다 401 을 다루면 언젠가 한 곳이 빠진다. */

type Phase =
  | { kind: "loading" }
  | { kind: "unreachable"; error: string }
  | { kind: "setup"; state: AuthState }
  | { kind: "login"; state: AuthState }
  /** 비밀번호는 맞았고 세션은 아직 없다 — 앱 코드를 기다린다(세션 = 2단계 통과). */
  | { kind: "totp"; pending: string }
  /** 정책이 2단계를 요구하는데 미등록 — 관문이 등록 문 밖을 403 으로 막는 상태다. */
  | { kind: "enroll"; me: Me }
  | { kind: "in"; me: Me };

type Ctx = { me: Me; logout: () => void; refreshMe: () => void };
const AuthCtx = createContext<Ctx | null>(null);

/** 들어온 사람. 문지기 밖(패턴 페이지의 표본 등)에서는 null 이다. */
export function useMe(): Ctx | null {
  return useContext(AuthCtx);
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 실패가 아닌 안내(세션 만료 · 첫 설정이 이미 끝남) — 「로그인 실패」 말머리를 달면 거짓말이다. */
  const [notice, setNotice] = useState<string | null>(null);
  const [lastUser, setLastUser] = useState("");
  /** hub 에서 돌아오다 실패한 갈래. 한 번 읽고 주소에서 지운다 — 새로고침마다 같은 실패를 다시
   *  보여주면 사람은 지금도 실패하는 줄 안다. */
  const [ssoError, setSsoError] = useState<string | null>(() => {
    const e = routeParam("sso_error");
    if (e) history.replaceState(null, "", `${location.pathname}#/`);
    return e;
  });

  const decide = useCallback(async () => {
    try {
      const state = await authApi.state();
      if (state.setupRequired) return setPhase({ kind: "setup", state });
      try {
        const me = await authApi.me();
        // 화면은 늘 사람이다 — 에이전트 토큰으로 이 화면을 열 길은 없다(브라우저는 Bearer 를 안 싣는다).
        if (me.kind === "human") {
          if (me.totpRequired && !me.totpEnabled) return setPhase({ kind: "enroll", me });
          return setPhase({ kind: "in", me });
        }
        return setPhase({ kind: "login", state });
      } catch (e) {
        if (e instanceof AuthError && e.status === 401) return setPhase({ kind: "login", state });
        throw e;
      }
    } catch (e) {
      setPhase({ kind: "unreachable", error: authErrText(e) });
    }
  }, []);

  useEffect(() => {
    watchUnauthorized();
    decide();
    // 세션이 끊겼다는 신호 — 지금 보던 화면을 버리고 로그인으로 간다. 들어와 있을 때만 다시 묻는다
    // (로그인 화면에서 또 받으면 같은 판정을 되풀이할 뿐이다).
    const onOut = () => {
      setPhase((p) => {
        if (p.kind === "in") {
          setNotice(tx("auth.sessionEnded"));
          setLastUser(p.me.username);
          decide();
          return { kind: "loading" };
        }
        return p;
      });
    };
    // 관리자가 정책을 켜면 **다음 요청부터** 403 이다 — 보던 화면에서 등록 화면으로 넘어간다.
    const onEnroll = () => {
      decide();
    };
    addEventListener(LOGGED_OUT, onOut);
    addEventListener(ENROLL_REQUIRED, onEnroll);
    return () => {
      removeEventListener(LOGGED_OUT, onOut);
      removeEventListener(ENROLL_REQUIRED, onEnroll);
    };
  }, [decide]);

  async function login(username: string, password: string, opts: { saveId: boolean; remember: boolean }) {
    setBusy(true);
    setError(null);
    setNotice(null);
    setSsoError(null);
    setLastUser(username);
    try {
      const res = await authApi.login({ username, password, remember: opts.remember });
      // 비밀번호가 받아들여진 뒤에만 저장한다(2단계 대기여도) — 틀린 아이디를 남겨 두지 않는다.
      writeSavedLoginId(opts.saveId ? username : null);
      if ("totpRequired" in res) {
        setPhase({ kind: "totp", pending: res.pendingToken });
        return;
      }
      await decide();
    } catch (e) {
      setError(authErrText(e));
    } finally {
      setBusy(false);
    }
  }

  async function setup(b: { token: string; username: string; password: string }) {
    setBusy(true);
    setError(null);
    try {
      await authApi.setup(b);
      // 첫 설정은 세션을 같이 세운다 — 바로 앱으로 들어간다.
      await decide();
    } catch (e) {
      // 진 쪽(409 · 이미 끝남)은 로그인 화면으로 보낸다 — 첫 설정 문은 이미 닫혔다.
      if (e instanceof AuthError && e.status === 409) {
        setLastUser(b.username);
        setNotice(authErrText(e));
        await decide();
      } else {
        setError(authErrText(e));
      }
    } finally {
      setBusy(false);
    }
  }

  const logout = useCallback(() => {
    authApi
      .logout()
      .catch(() => undefined)
      .finally(() => {
        setError(null);
        setNotice(null);
        decide();
      });
  }, [decide]);

  const refreshMe = useCallback(() => {
    authApi
      .me()
      .then((me) => me.kind === "human" && setPhase({ kind: "in", me }))
      .catch(() => undefined);
  }, []);

  /** 2단계 코드. 거절되면 그대로 던진다 — 안쪽 조각이 서버 문장을 그린다. */
  async function submitTotp(pending: string, code: string) {
    await authApi.loginTotp({ pendingToken: pending, code });
    setError(null);
    setNotice(null);
    await decide();
  }

  const enrollFns = {
    onSetup: authApi.totpSetup,
    onConfirm: async (code: string) => {
      await authApi.totpConfirm(code);
    },
    onDisable: async (password: string) => {
      await authApi.totpDisable(password);
    },
    onEnrolled: () => {
      decide();
    },
  };

  if (phase.kind === "in") {
    return <AuthCtx.Provider value={{ me: phase.me, logout, refreshMe }}>{children}</AuthCtx.Provider>;
  }
  return (
    <AuthFrame>
      {phase.kind === "loading" ? (
        <p className="muted" role="status">
          <Spinner size="sm" /> {tx("auth.checking")}
        </p>
      ) : phase.kind === "unreachable" ? (
        <div className="authframe__unreachable">
          <Alert tone="error">{tx("auth.stateFailed", { error: phase.error })}</Alert>
          <Button onClick={decide}>{tx("common.action.retry")}</Button>
        </div>
      ) : phase.kind === "totp" ? (
        <TotpLoginCard
          onSubmit={(code) => submitTotp(phase.pending, code)}
          onRestart={() => {
            setError(null);
            decide();
          }}
        />
      ) : phase.kind === "enroll" ? (
        <TotpEnrollGate {...enrollFns} title={tx("totp.enrollNeeded")} logoutLabel={tx("common.action.logout")} onLogout={logout} />
      ) : phase.kind === "setup" ? (
        <SetupCard busy={busy} error={error} onSubmit={setup} />
      ) : (
        <LoginCard
          mode={phase.state.loginMode}
          issuer={phase.state.ssoIssuer}
          busy={busy}
          error={error}
          notice={notice}
          ssoError={ssoError}
          initialUser={lastUser}
          onSubmit={login}
          onHub={() => {
            // 브라우저째로 간다 — api 가 state 쿠키를 두고 hub 로 보낸다. fetch 로 부르면 그 쿠키와
            // hub 의 화면이 이 창에 안 남는다.
            location.href = SSO_LOGIN_URL;
          }}
        />
      )}
    </AuthFrame>
  );
}
